/* Native fixed/absolute layout for Euronavigate desktop Product Page.
   Throttled compositor-only translation in paced stages prevents layout thrash;
   pinned stages use native fixed positioning. */
(() => {
  'use strict';
  // Restore the old Ignite page-led scrolling with native CSS sticky geometry.
  // No scroll event transforms, scrollTop forcing, or custom pacing.
  const nativeMedia = matchMedia('(min-width: 750px)');
  const nativeBound = new WeakSet();
  function mountNativePanels() {
    document.querySelectorAll(
      '.section-product-page .product.grid.en-product-two-column > .product__info-wrapper > product-info.product__column-sticky'
    ).forEach(info => {
      if (nativeBound.has(info)) return;
      nativeBound.add(info);
      let lastHeight = 0;
      let observer;
      function updateHeight() {
        if (!info.isConnected) { observer?.disconnect(); return; }
        if (!nativeMedia.matches) {
          info.classList.remove('en-natural-page-scroll');
          info.style.removeProperty('--en-natural-panel-height');
          lastHeight = 0;
          return;
        }
        info.classList.add('en-natural-page-scroll');
        const height = Math.ceil(info.getBoundingClientRect().height);
        if (height > 0 && height !== lastHeight) {
          info.style.setProperty('--en-natural-panel-height', height + 'px');
          lastHeight = height;
        }
      }
      observer = new ResizeObserver(updateHeight);
      nativeMedia.addEventListener('change', updateHeight);
      updateHeight();
      observer.observe(info);
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mountNativePanels, {once: true});
  } else {
    mountNativePanels();
  }
  // EN_FOG_VISIBLE_HEADER_STICKY_20261026: follow the actual visible header,
  // not the cached header-height CSS variable (which can overstate its height).
  const fogMedia = matchMedia('(min-width: 750px)');
  const fogGridSelector = '.section-product-page .product.grid.en-product-two-column[data-product-handle="superior-yellow-beam-led-fog-light-assembly-for-land-rover-discovery-4"]';
  let fogFrame = 0;
  let fogObservedInfo = null;
  const fogObserver = new ResizeObserver(() => scheduleFogPanel());
  function updateFogPanel() {
    fogFrame = 0;
    const grid = document.querySelector(fogGridSelector);
    const info = grid?.querySelector(':scope > .product__info-wrapper > product-info.product__column-sticky');
    if (!info) return;
    if (info !== fogObservedInfo) {
      fogObserver.disconnect();
      fogObservedInfo = info;
      fogObserver.observe(info);
    }
    if (!fogMedia.matches) {
      info.style.removeProperty('top');
      return;
    }
    let headerBottom = 0;
    for (const selector of ['.announcement-bar-sticky', '.section-header']) {
      const header = document.querySelector(selector);
      if (!header) continue;
      const style = getComputedStyle(header);
      const rect = header.getBoundingClientRect();
      if (style.display === 'none' || style.visibility === 'hidden' ||
          Number(style.opacity) === 0 || rect.bottom <= 0 ||
          rect.top >= innerHeight || rect.height <= 0) continue;
      headerBottom = Math.max(headerBottom, rect.bottom);
    }
    const panelHeight = Math.ceil(info.getBoundingClientRect().height);
    if (!panelHeight) return;
    // Match the verified native scroll stop of the working fog-lamp page (857px panel + 16px guard).
    // Keep the anchor stable if asynchronous payment content changes this short panel's height.
    const top = Math.round(Math.min(headerBottom + 12, innerHeight - 873));
    if (info.style.top !== top + 'px' || info.style.getPropertyPriority('top') !== 'important') {
      info.style.setProperty('top', top + 'px', 'important');
    }
  }
  function scheduleFogPanel() {
    if (!fogFrame) fogFrame = requestAnimationFrame(updateFogPanel);
  }
  window.addEventListener('scroll', scheduleFogPanel, { passive: true });
  window.addEventListener('resize', scheduleFogPanel, { passive: true });
  document.addEventListener('product-info:loaded', scheduleFogPanel);
  document.addEventListener('shopify:section:load', scheduleFogPanel);
  fogMedia.addEventListener('change', scheduleFogPanel);
  scheduleFogPanel();
  document.addEventListener('product-info:loaded', () => requestAnimationFrame(mountNativePanels));
  return;
  const media = window.matchMedia('(min-width: 750px)');
  const docTop = node => node.getBoundingClientRect().top + scrollY;
  function mount(grid) {
    if (grid.dataset.enSyncBound) return;
    const wrap = grid.querySelector(':scope > .product__info-wrapper');
    const details = grid.querySelector(':scope > .product__media-submedia-wrapper');
    const info = wrap?.querySelector('product-info.product__column-sticky');
    if (!wrap || !details || !info) return;
    grid.dataset.enSyncBound = '1';
    let bounds = null, frame = 0, sizeFrame = 0, activeMode = '', lastAbs = null;
    let lastScrollAt = -Infinity, deferredMeasure = 0;
    let panelWidth = 0;
    const reservedBlocks = new Set();

    function clearReservations() {
      reservedBlocks.forEach(block => block.style.removeProperty('--en-sync-block-height'));
      reservedBlocks.clear();
    }

    function update() {
      frame = 0;
      if (!media.matches || !bounds || !grid.isConnected) return;
      const {start, desc, end, h, gap, bottomGap} = bounds;
      const y = window.scrollY;
      const first = Math.max(start, y + gap);
      let clamped;
      if (bounds.discovery4) {
        const {faqY} = bounds.discovery4;
        const transitionStart = faqY - gap;
        const visibleHeight = innerHeight - gap - bottomGap;
        const travel = Math.max(0, h - visibleHeight);
        // The FAQ-to-Shop Safely interval is short. Moving the entire panel
        // through that interval hides Bundle and Recommendations too quickly.
        // Instead reveal the right-column content steadily over the long media.
        const scrollBudget = Math.max(1, end - innerHeight + bottomGap - transitionStart);
        const holdDistance = Math.min(650, Math.max(180, scrollBudget * 0.1));
        const pace = travel > 0
          ? Math.min(1, Math.max(0.6, travel / Math.max(1, scrollBudget - holdDistance)))
          : 0;
        const progress = Math.min(travel, Math.max(0, y - transitionStart) * pace);
        // Stage 1: hold the top through Description.
        // Stage 2: move Bundle and Recommendations at a readable pace over Media.
        // Stage 3: pin the panel bottom, then release at the product-grid end.
        const wanted = y < transitionStart ? first : y + gap - progress;
        clamped = Math.max(start, Math.min(wanted, end - h - bottomGap));
      } else {
        const target = h <= innerHeight - gap - bottomGap
          ? first
          : Math.max(Math.min(desc, first), y + innerHeight - bottomGap - h);
        const limit = Math.max(start, end - h - bottomGap);
        clamped = Math.max(start, Math.min(target, limit));
      }
      const limit = Math.max(start, end - h - bottomGap);
      // Keep Discovery 4 in one layout mode. Switching between fixed and
      // absolute was resizing the product grid and moving the scroll anchor.
      // The same clamped coordinate retains 0.6x pacing and both stop points.
      let mode = 'absolute', absolute = clamped - start;
      if (!bounds.discovery4) {
        if (clamped <= start + 1) absolute = 0;
        else if (clamped >= limit - 1) absolute = limit - start;
        else if (Math.abs(clamped - (y + gap)) <= 1) mode = 'top';
        else if (h > innerHeight - gap - bottomGap &&
                 Math.abs(clamped - (y + innerHeight - bottomGap - h)) <= 1) mode = 'bottom';
      }
      if (mode !== activeMode) {
        info.setAttribute('data-en-sync-mode', mode);
        activeMode = mode;
      }
      if (mode === 'absolute') {
        const rounded = Math.round(absolute);
        if (rounded !== lastAbs) {
          info.style.setProperty('--en-sync-abs-top', rounded + 'px');
          lastAbs = rounded;
        }
      }
    }
    function schedule() { if (!frame) frame = requestAnimationFrame(update); }

    function measure() {
      if (!grid.isConnected) return;
      // Discovery 4: keep the current geometry snapshot stable throughout a
      // wheel/trackpad scroll burst. Late app/media hydration must not change
      // the panel's scroll anchors while the customer is moving the page.
      // Commit real content-size changes once scrolling has been idle.
      if (bounds?.discovery4 && media.matches && performance.now() - lastScrollAt < 260) {
        clearTimeout(deferredMeasure);
        deferredMeasure = setTimeout(() => { deferredMeasure = 0; measure(); }, 275);
        return;
      }
      if (deferredMeasure) { clearTimeout(deferredMeasure); deferredMeasure = 0; }
      if (!media.matches) {
        clearReservations(); panelWidth = 0;
        grid.classList.remove('en-scroll-sync-enabled');
        bounds = null; activeMode = ''; lastAbs = null;
        info.removeAttribute('data-en-sync-mode');
        info.style.removeProperty('--en-sync-abs-top');
        wrap.style.removeProperty('--en-sync-panel-height');
        wrap.style.removeProperty('--en-sync-panel-width');
        wrap.style.removeProperty('--en-sync-panel-left');
        return;
      }
      const width = wrap.getBoundingClientRect().width;
      if (width !== panelWidth) { clearReservations(); panelWidth = width; }
      // Hydration may shrink an already visible card (wallet fallback, bundle,
      // recommendations). Retain its measured allocation at this column width;
      // do not guess an app height or constrain content that needs more room.
      [...info.children].filter(block => block.classList.contains('product__info-container')).forEach(block => {
        const height = block.getBoundingClientRect().height;
        const reserved = parseFloat(block.style.getPropertyValue('--en-sync-block-height')) || 0;
        if (height > reserved) block.style.setProperty('--en-sync-block-height', height + 'px');
        reservedBlocks.add(block);
      });
      // Preserve the natural column height before changing layout positions.
      const h = info.getBoundingClientRect().height;
      if (!h) return;
      wrap.style.setProperty('--en-sync-panel-height', Math.ceil(h) + 'px');
      grid.classList.add('en-scroll-sync-enabled');
      const rect = wrap.getBoundingClientRect();
      wrap.style.setProperty('--en-sync-panel-left', rect.left + 'px');
      wrap.style.setProperty('--en-sync-panel-width', rect.width + 'px');
      const gap = Math.max(16, (parseFloat(getComputedStyle(document.documentElement)
        .getPropertyValue('--header-height-live')) || 0) + 16);
      bounds = {
        start: docTop(wrap), desc: Math.max(docTop(wrap), docTop(details)),
        end: docTop(grid) + grid.getBoundingClientRect().height,
        h, gap, bottomGap: 16
      };
      // The Discovery 4 catalogue alone uses FAQ and story-media scroll stages.
      const isD4 = !!details.querySelector('.en-discovery4-description');
      if (isD4) {
        const faq = [...details.querySelectorAll('custom-accordion')].find(el =>
          /frequently asked questions/i.test(el.querySelector('summary')?.textContent || '')
        ) || details.querySelector('.en-discovery4-description')?.closest('.product__info-panel')?.querySelector('custom-accordion');
        const story = details.querySelector('.en-product-story-native');
        const safely = story?.previousElementSibling;
        if (faq && story && safely) {
          const faqY = docTop(faq);
          const safelyY = docTop(safely);
          const storyY = docTop(story);
          if (faqY > bounds.start && safelyY > faqY && storyY > safelyY) {
            bounds.discovery4 = {faqY, safelyY, storyY};
          }
        }
      }
      schedule();
    }
    const ro = new ResizeObserver(() => {
      if (sizeFrame) return;
      sizeFrame = requestAnimationFrame(() => {sizeFrame = 0; measure();});
    });
    // The grid and wrapper change geometry as the panel switches fixed/absolute.
    // Observe content dimensions only to avoid scroll-induced remeasurement loops.
    [info, details].forEach(el => ro.observe(el));
    const mo = new MutationObserver(() => {
      if (sizeFrame) return;
      sizeFrame = requestAnimationFrame(() => {sizeFrame = 0; measure();});
    });
    mo.observe(info, {subtree:true, childList:true});
    // Apply state transitions immediately at section boundaries; pinned
    // positions remain native CSS fixed and do not need per-frame transforms.
    window.addEventListener('scroll', () => { lastScrollAt = performance.now(); update(); }, {passive: true});
    window.addEventListener('resize', measure);
    media.addEventListener('change', measure);
    measure();
  }
  function boot() {
    document.querySelectorAll('.section-product-page .product.grid.en-product-two-column').forEach(mount);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, {once:true});
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
